import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SwayyCategoriesStore } from "../../../stores/SwayyCategoriesStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import { SwayyPageHeader, SwayyTableEmptyState } from "../../components/SwayyPageShell";

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 32,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    height: 32,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const SwayyCategories = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef(new SwayyCategoriesStore());
  const store = storeRef.current;
  const debouncedSearch = useDebounce(store.searchDraft, 400);
  const [createOpen, setCreateOpen] = useState(false);
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  useEffect(() => {
    if (!canViewCatalog) return;
    void store.loadTree().then((error) => {
      if (error) {
        showErrorRef.current("Error", "Failed to load categories.");
      }
    });
    return () => store.dispose();
  }, [canViewCatalog, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    store.setSearch(debouncedSearch);
  }, [canViewCatalog, debouncedSearch, store]);

  const handleCreate = async () => {
    if (!store.canCreateCategory) {
      showError("Validation", "Name and SKU code are required.");
      return;
    }
    const result = await store.createCategory();
    if (result.error) {
      showError("Error", "Failed to create category.");
      return;
    }
    setCreateOpen(false);
    showSuccess("Category created.");
    if (result.categoryId) navigate(`/categories/${result.categoryId}`);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    store.resetCreateForm();
  };

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <SwayyPageHeader
        title="Categories"
        description="Create categories and link options so products can use them."
        actions={
          canEditCatalog ? (
            <AppButton
              label="Add category"
              variant="primary"
              onClick={() => setCreateOpen(true)}
              customStyles={primaryBtnSx}
            />
          ) : undefined
        }
      />

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
        }}
      >
        <TextField
          size="small"
          placeholder="Search categories"
          value={store.searchDraft}
          onChange={(event) => store.setSearchDraft(event.target.value)}
          fullWidth
          sx={{
            maxWidth: 360,
            fontFamily: swayyFont,
            "& .MuiInputBase-root": { height: 32, fontSize: 13 },
          }}
        />
      </Paper>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          overflow: "hidden",
        }}
      >
        {store.listState === "loading" || store.listState === "idle" ? (
          <Box sx={{ p: 2 }}>
            <Skeleton height={40} />
            <Skeleton height={40} />
          </Box>
        ) : store.listState === "error" ? (
          <SwayyTableEmptyState
            title="Couldn't load categories"
            description={store.errorMessage || "Network Error"}
            actionLabel="Try again"
            onAction={() => {
              void store.loadTree().then((error) => {
                if (error) {
                  showError("Error", "Failed to load categories.");
                }
              });
            }}
            danger
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.flatCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <CustomTypography
                      variant="body2"
                      color={swayyColors.textSecondary}
                    >
                      No categories yet.
                    </CustomTypography>
                  </TableCell>
                </TableRow>
              ) : (
                store.flatCategories.map((category) => (
                  <TableRow
                    key={category.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/categories/${category.id}`)}
                  >
                    <TableCell>
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        fontWeight={700}
                        sx={{ pl: category.depth * 2 }}
                      >
                        {category.name}
                      </CustomTypography>
                    </TableCell>
                    <TableCell>{category.skuCode || "—"}</TableCell>
                    <TableCell>
                      {category.isActive ? "Active" : "Inactive"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add category</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <OutlinedInput
              value={store.createForm.name}
              onChange={(event) =>
                store.updateCreateForm("name", event.target.value)
              }
              placeholder="Category name"
              fullWidth
              sx={inputSx}
            />
            <OutlinedInput
              value={store.createForm.skuCode}
              onChange={(event) =>
                store.updateCreateForm("skuCode", event.target.value)
              }
              placeholder="SKU code"
              fullWidth
              sx={inputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseCreate}
            customStyles={secondaryBtnSx}
          />
          <AppButton
            label={store.isSaving ? "Creating..." : "Create"}
            variant="primary"
            disabled={store.isSaving}
            onClick={() => void handleCreate()}
            customStyles={primaryBtnSx}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default observer(SwayyCategories);
